import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Zap, TrendingUp, RotateCcw, Target, Activity, BarChart3 } from 'lucide-react';
import BreathtakingCard from './shared/BreathtakingCard.js';
import { cardVariants } from './shared/CardAnimations.js';
import { useWorkspace } from '../index.js';

interface StrategyAttributionMetrics {
  pnlAttributionByClass: {
    [key: string]: number;
  };
  alphaCategoryContribution: {
    [key: string]: number;
  };
  volatilityAdjustedReturn: {
    [key: string]: number;
  };
  rotationEvents: number;
  strategyConfidenceScore: number;
  executionGuardTriggers: number;
}

const StrategyAttributionPanel: React.FC = () => {
  const { workspaceId } = useWorkspace();
  const [attributionData, setAttributionData] = useState<StrategyAttributionMetrics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAttributionMetrics();
    const interval = setInterval(fetchAttributionMetrics, 30000);
    return () => clearInterval(interval);
  }, [workspaceId]);

  const fetchAttributionMetrics = async () => {
    try {
      const response = await fetch(`/api/arcoversight/metrics/strategy/attribution?workspaceId=${workspaceId}`);
      const result = response.ok ? await response.json() : null;

      setAttributionData({
        pnlAttributionByClass: result?.data?.pnlAttributionByClass || {
          'Trend': 0.42,
          'MeanReversion': 0.28,
          'Composite': 0.20,
          'Momentum': 0.10
        },
        alphaCategoryContribution: result?.data?.alphaCategoryContribution || {
          'Technical': 0.35,
          'Fundamental': 0.25,
          'Sentiment': 0.20,
          'Macro': 0.20
        },
        volatilityAdjustedReturn: result?.data?.volatilityAdjustedReturn || {
          'Trend': 1.85,
          'MeanReversion': 1.42,
          'Composite': 1.67,
          'Momentum': 1.23
        },
        rotationEvents: result?.data?.rotationEvents || 12,
        strategyConfidenceScore: result?.data?.strategyConfidenceScore || 0.78,
        executionGuardTriggers: result?.data?.executionGuardTriggers || 3
      });
    } catch (error) {
      console.error('Failed to fetch strategy attribution metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-400" />
          <span className="text-brand-text text-lg [text-shadow:_0_1px_2px_rgb(0_0_0_/_60%)]">Loading strategy data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 min-h-screen">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex items-center justify-between"
      >
        <h1 className="text-3xl font-bold text-yellow-400">Strategy &amp; Attribution Analysis</h1>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse" />
          <span className="text-sm text-slate-300">IP Protected</span>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.5 }}
      >
        <h2 className="text-xl font-semibold text-white mb-4 [text-shadow:_0_1px_2px_rgb(0_0_0_/_60%)]">Strategy Performance Attribution</h2>
        <motion.div 
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6"
          variants={cardVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div custom={0} variants={cardVariants}>
            <BreathtakingCard
              title="Rotation Events"
              value={attributionData?.rotationEvents?.toString() || '0'}
              subtitle="Last 30 Days"
              variant="performance"
              size="md"
              trend={attributionData && attributionData.rotationEvents < 15 ? 'up' : 'neutral'}
              icon={<RotateCcw className="w-6 h-6 text-blue-400" />}
            />
          </motion.div>

          <motion.div custom={1} variants={cardVariants}>
            <BreathtakingCard
              title="Confidence Score"
              value={`${((attributionData?.strategyConfidenceScore || 0) * 100).toFixed(1)}%`}
              subtitle="Strategy Confidence"
              variant="performance"
              size="md"
              trend={attributionData && attributionData.strategyConfidenceScore > 0.7 ? 'up' : 'neutral'}
              icon={<Target className="w-6 h-6 text-green-400" />}
            />
          </motion.div>

          <motion.div custom={2} variants={cardVariants}>
            <BreathtakingCard
              title="Guard Triggers"
              value={attributionData?.executionGuardTriggers?.toString() || '0'}
              subtitle="Execution Filtered"
              variant="performance"
              size="md"
              trend={attributionData && attributionData.executionGuardTriggers < 5 ? 'up' : 'down'}
              icon={<Activity className="w-6 h-6 text-orange-400" />}
            />
          </motion.div>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.5 }}
        className="grid grid-cols-1 xl:grid-cols-2 gap-4 md:gap-6"
      >
        <BreathtakingCard
          title="PnL Attribution by Strategy Class"
          variant="performance"
          size="lg"
          className="col-span-1"
        >
          <div className="space-y-4">
            <div className="text-sm text-slate-400 mb-4">
              Strategy performance bucketed by anonymized class labels
            </div>
            {attributionData && Object.entries(attributionData.pnlAttributionByClass)
              .sort(([,a], [,b]) => b - a)
              .map(([strategy, contribution], index) => (
              <div key={strategy} className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-300 font-medium">{strategy} Class</span>
                  <span className="text-white font-bold">{(contribution * 100).toFixed(1)}%</span>
                </div>
                <div className="w-full bg-slate-700 rounded-full h-3">
                  <motion.div 
                    className="h-3 rounded-full bg-gradient-to-r from-purple-500 to-blue-500"
                    initial={{ width: 0 }}
                    animate={{ width: `${contribution * 100}%` }}
                    transition={{ delay: 0.6 + index * 0.1, duration: 0.8 }}
                  />
                </div>
              </div>
            ))}
          </div>
        </BreathtakingCard>

        <BreathtakingCard
          title="Alpha Category Contribution"
          variant="performance"
          size="lg"
          className="col-span-1"
        >
          <div className="space-y-4">
            <div className="text-sm text-slate-400 mb-4">
              Alpha source attribution with IP protection
            </div>
            {attributionData && Object.entries(attributionData.alphaCategoryContribution)
              .sort(([,a], [,b]) => b - a)
              .map(([category, contribution], index) => (
              <div key={category} className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-300 font-medium">{category} Alpha</span>
                  <span className="text-white font-bold">{(contribution * 100).toFixed(1)}%</span>
                </div>
                <div className="w-full bg-slate-700 rounded-full h-3">
                  <motion.div 
                    className="h-3 rounded-full bg-gradient-to-r from-green-500 to-teal-500"
                    initial={{ width: 0 }}
                    animate={{ width: `${contribution * 100}%` }}
                    transition={{ delay: 0.8 + index * 0.1, duration: 0.8 }}
                  />
                </div>
              </div>
            ))}
          </div>
        </BreathtakingCard>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6, duration: 0.5 }}
      >
        <h2 className="text-xl font-semibold text-white mb-4 [text-shadow:_0_1px_2px_rgb(0_0_0_/_60%)]">Volatility-Adjusted Returns by Class</h2>
        <motion.div 
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6"
          variants={cardVariants}
          initial="hidden"
          animate="visible"
        >
          {attributionData && Object.entries(attributionData.volatilityAdjustedReturn)
            .sort(([,a], [,b]) => b - a)
            .map(([strategy, ratio], index) => (
            <motion.div key={strategy} custom={index} variants={cardVariants}>
              <BreathtakingCard
                title={`${strategy} Class`}
                value={ratio.toFixed(2)}
                subtitle="Vol-Adjusted Return"
                variant="performance"
                size="md"
                trend={ratio > 1.5 ? 'up' : ratio > 1.0 ? 'neutral' : 'down'}
                icon={<TrendingUp className="w-6 h-6 text-purple-400" />}
              />
            </motion.div>
          ))}
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8, duration: 0.5 }}
        className="grid grid-cols-1 gap-4 md:gap-6"
      >
        <BreathtakingCard
          title="Strategy Execution Intelligence"
          variant="performance"
          size="lg"
          className="col-span-1"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-yellow-400" />
                <span className="text-sm font-medium text-slate-300">Strategy Rotation</span>
              </div>
              <div className="text-2xl font-bold text-white">
                {attributionData?.rotationEvents || 0} events
              </div>
              <div className="text-xs text-slate-400">
                Automated strategy switches based on market conditions
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Target className="w-5 h-5 text-green-400" />
                <span className="text-sm font-medium text-slate-300">Confidence Trend</span>
              </div>
              <div className="text-2xl font-bold text-white">
                {((attributionData?.strategyConfidenceScore || 0) * 100).toFixed(0)}%
              </div>
              <div className="text-xs text-slate-400">
                Weighted execution confidence across all strategies
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-blue-400" />
                <span className="text-sm font-medium text-slate-300">Guard Efficiency</span>
              </div>
              <div className="text-2xl font-bold text-white">
                {attributionData?.executionGuardTriggers || 0} filtered
              </div>
              <div className="text-xs text-slate-400">
                Trades blocked or downgraded by execution guards
              </div>
            </div>
          </div>

          <div className="mt-6 p-4 bg-slate-800/50 rounded-lg border border-slate-700">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 bg-purple-400 rounded-full" />
              <span className="text-sm font-medium text-slate-300">IP Protection Notice</span>
            </div>
            <div className="text-xs text-slate-400">
              All strategy logic, position sizing formulas, and execution routes are redacted. 
              Only anonymized class labels and aggregated performance statistics are displayed.
            </div>
          </div>
        </BreathtakingCard>
      </motion.div>
    </div>
  );
};

export default StrategyAttributionPanel;
